import { Injectable } from '@angular/core';
import { Auth } from 'src/providers/auth/auth';
import { Client } from 'src/providers/client/client';
import { Me } from 'src/providers/me/me';
import { Events } from 'src/providers/events/events';
import { OnboardingProvider } from 'src/providers/onboarding/onboarding.provider';
import { AboutThisPageService } from 'src/providers/about-this-page/about-this-page.service';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { PosthogService } from 'src/shared/services/posthog.service';
import { BookService } from 'src/shared/services/book.service';

@Injectable({
  providedIn: 'root'
})
export class GuestAccountService {
  private static readonly GUEST_EMAIL_DOMAIN = '@placeholder.tidy.com';
  private static readonly GUEST_PASSWORD = 'GuestPass123!';

  constructor(
    private auth: Auth,
    private client: Client,
    private events: Events,
    private me: Me,
    private onboardingProvider: OnboardingProvider,
    private aboutThisPageService: AboutThisPageService,
    private storage: TidyStorage,
    private posthogService: PosthogService,
    private bookService: BookService
  ) {}

  generateGuestEmail(): string {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let randomPart = '';
    for (let i = 0; i < 12; i++) {
      randomPart += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    const timestamp = Date.now().toString(36);
    const uuid = `${randomPart}${timestamp}`;
    return `${uuid}${GuestAccountService.GUEST_EMAIL_DOMAIN}`;
  }

  async createGuestAccount(accountType?: string): Promise<any> {
    try {
      const guestViewType = localStorage.getItem('guest_view_type');
      const resolvedAccountType = accountType || guestViewType || 'rental';
      localStorage.removeItem('tidy_token');
      localStorage.removeItem('tidy_token_type');
      localStorage.removeItem('customer_id');
      const email = this.generateGuestEmail();
      const password = GuestAccountService.GUEST_PASSWORD;
      const credentials = {
        username: email,
        password
      };
      await this.auth.login(credentials, 'signUp', null);
      const customerData = {
        customer: {
          booked_via: 'Self Booking',
          email,
          first_name: 'Guest',
          last_name: '',
          password,
          account_type: resolvedAccountType
        }
      };
      const customerResponse = await this.client.getCustomers(customerData);
      localStorage.setItem('customer_id', customerResponse.id);
      if (resolvedAccountType === 'rental') {
        // New rental account → gate booking until a setup call is scheduled
        // (see DemoBlockService.shouldGateNewRental).
        localStorage.setItem('justCreatedAccount', 'true');
      }
      localStorage.setItem('email', email);
      localStorage.setItem('firstName', 'Guest');
      localStorage.setItem('lastName', 'User');
      this.auth.setSession();
      await this.auth.login(credentials, 'signUpPassword', null);
      const account = await this.me.load(true);
      const customerObj: any = account;
      this.auth.setAccountTypeStorage(customerObj.customer_account.account_type);
      this.events.publish('menu:open');
      this.events.publish('updateMenu');
      return customerResponse;
    } catch (error) {
      localStorage.removeItem('tidy_token');
      localStorage.removeItem('tidy_token_type');
      localStorage.removeItem('customer_id');
      localStorage.removeItem('email');
      localStorage.removeItem('firstName');
      localStorage.removeItem('lastName');

      throw error;
    }
  }

  // Creates a guest account for the given type and runs all post-creation setup
  // (onboarding goals, about-this-page data, PostHog identify, auth state). Shared
  // by the auth modal and the native landing page so the guest flow stays identical.
  async completeGuestSetup(type: string): Promise<void> {
    localStorage.setItem('isRentalClient', type === 'rental' ? 'true' : 'false');
    localStorage.setItem('guest_view_type', type);
    this.events.publish('auth:type-selected');
    try {
      await this.createGuestAccount(type);
      localStorage.setItem('hasCard', 'false');
      await this.storage.save('isGuestAccount', true);
      await this.aboutThisPageService.fetchAndStoreAboutThisPageData();
      await this.setOnboardingGoals();
      const customerId = localStorage.getItem('customer_id');
      const email = localStorage.getItem('email');
      if (customerId && email) {
        await this.posthogService.identify(Number(customerId), email, { account_type: type });
        await this.posthogService.reloadFeatureFlags();
        await this.bookService.cacheBookingViewV2Variant();
      }
      this.auth.authenticated.set(true);
      this.events.publish('auth:complete');
    } catch (err) {
      localStorage.removeItem('isRentalClient');
      localStorage.removeItem('guest_view_type');
      throw err;
    }
  }

  private async getGoalsPayload() {
    const goalsToPick = await this.onboardingProvider.getGoalsToPickList();
    const payload = goalsToPick.map((goal) => ({
      user_goal_type_id: goal.id,
      chosen: true
    }));
    return {
      user_goals: payload
    };
  }

  private async setOnboardingGoals(retries = 0): Promise<void> {
    try {
      const goalsPayload = await this.getGoalsPayload();
      await this.onboardingProvider.saveChosenGoals(goalsPayload);
      await this.onboardingProvider.getGoalsFromServer();
      localStorage.setItem('showOnboarding', 'true');
      this.events.publish('goals:updated');
    } catch (err) {
      console.error('setOnboardingGoals failed:', err);
      if (retries < 3) {
        setTimeout(() => this.setOnboardingGoals(retries + 1), 5000);
      } else {
        localStorage.removeItem('tryGoalSetup');
      }
    }
  }
}
